import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export function CsvImportCard({ onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a CSV file first");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/users/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created} created · ${data.skipped} skipped`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const csvTemplate = "first_name,discord_name,email,role,title,department,location,timezone,start_date,birthdate,phone\nAlex,alex_disc,alex@example.com,employee,Community Mod,Ops,Remote — US,America/New_York,2024-03-15,1996-08-21,\n";
  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "team-import-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card-surface admin-card p-5" data-testid="csv-import-card">
      <div className="flex items-center gap-2 mb-2">
        <Upload size={18} className="text-[color:var(--accent)]" />
        <h3 className="text-lg font-bold">Bulk-import from CSV</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">Upload a spreadsheet to create many teammates at once. Existing emails are skipped.</p>
      <button
        type="button"
        onClick={downloadTemplate}
        data-testid="csv-template"
        className="text-xs text-[color:var(--accent)] hover:underline inline-flex items-center gap-1 mb-3"
      >
        Download template
      </button>
      <form onSubmit={submit} className="flex gap-2 items-center">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0])}
          data-testid="csv-file"
          className="text-xs text-slate-300 flex-1 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#242c3d] file:text-slate-200 file:cursor-pointer"
        />
        <button
          disabled={busy || !file}
          data-testid="csv-submit"
          className="px-3 py-2 rounded-lg bg-[color:var(--accent)] text-black font-bold text-sm disabled:opacity-60"
        >
          {busy ? "Importing…" : "Import"}
        </button>
      </form>
      {result && (
        <div className="mt-3 text-xs text-slate-300 space-y-1" data-testid="csv-result">
          <div>✓ Created: <span className="font-bold text-[color:var(--accent)]">{result.created}</span> · Skipped: {result.skipped} · Errors: {result.errors?.length || 0}</div>
          {result.errors?.slice(0, 5).map((e, i) => (
            <div key={`err-${e.row}-${i}`} className="text-rose-400">Row {e.row}: {e.error}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CsvImportCard;
